/**
 * Work Table Phase C2 — Universal Drag Payload
 *
 * ONE canonical drag payload that every source panel emits and the Work
 * Table drop handler consumes. Sources still emit their own legacy
 * payloads alongside this one so nothing regresses in other consumers
 * (MoodBoardCanvas, StudioEditor, etc.).
 *
 * Payload shape (kept intentionally small — everything else lives on
 * the Phase C1 canonical item and can be resolved via GET /api/items):
 *
 *   {
 *     item_id:      string,        // globally unique — always source_store-prefixed
 *     source_store: string,        // one of: unsorted_items | clips | moodboard_uploads
 *                                  //         | library_items | client_inspirations | file_hub
 *     type:         string,        // canonical: image | file | clip | color | material | note | link
 *     name:         string | null,
 *     thumbnail:    string | null, // preview image URL
 *     source_url:   string | null, // where it came from on the web
 *     spec_num:     string | null,
 *   }
 *
 * MIME: `application/keymade-item` (never rename — every source and
 * every consumer must agree byte-for-byte).
 *
 * Multi-tenant safety: the payload is authored client-side by the
 * source panel, which is already tenant-scoped (LibraryHub only shows
 * this company's items, Staging only shows this user's inbox, etc.).
 * The Work Table item stored on drop is a DENORMALIZED SNAPSHOT — the
 * server never dereferences `ref_item_id` at render time, so a forged
 * payload cannot leak cross-tenant data.  Phase C3+ write paths that
 * DO server-resolve `ref_item_id` MUST company-scope every lookup.
 */

export const KEYMADE_ITEM_MIME = 'application/keymade-item';

/**
 * DP80 Part 4 — the placement reference, in one shape, for the paths that do
 * NOT come through a unified drag payload (a local file drop, a pasted URL, a
 * copy of an existing item). `store` names where the thing lives; `id` may be
 * null when nothing was stored yet (a URL drop references no row).
 */
export function makePlacementRef({ store, id = null, via, specNum = null }) {
  const ref = {
    source_store: store,
    placed_via: via,
    placed_at: new Date().toISOString(),
  };
  if (id) ref.ref_item_id = String(id).startsWith(`${store}:`) ? String(id) : `${store}:${id}`;
  if (specNum) ref.spec_num = specNum;
  return ref;
}

// The five C1 island stores plus 'file_hub' for the workspace file list
// (the file-hub items are not yet in GET /api/items — see the C1
// docstring TRACKED GAP: work_tables/file_hub content stays local until
// C2 write paths / C3 consolidation).
export const KEYMADE_SOURCE_STORES = new Set([
  'unsorted_items',
  'clips',
  'moodboard_uploads',
  'library_items',
  'client_inspirations',
  'file_hub',
]);

export const KEYMADE_ITEM_TYPES = new Set([
  'image',
  'file',
  'clip',
  'color',
  'material',
  'note',
  'link',
]);

/**
 * Build the canonical payload from a source's native item shape.
 * Prefixes `item_id` with `source_store:` when the raw id doesn't
 * already carry that prefix — guarantees global uniqueness across
 * stores that may share numeric/uuid namespaces.
 */
export function makeUnifiedPayload({
  item_id,
  source_store,
  type,
  name = null,
  thumbnail = null,
  source_url = null,
  spec_num = null,
}) {
  if (!item_id || !source_store || !type) {
    throw new Error(
      `keymade-item payload missing required field(s): item_id=${item_id}, source_store=${source_store}, type=${type}`,
    );
  }
  const rawId = String(item_id);
  const prefix = `${source_store}:`;
  const globallyUnique = rawId.startsWith(prefix) ? rawId : `${prefix}${rawId}`;
  return {
    item_id: globallyUnique,
    source_store,
    type,
    name: name || null,
    thumbnail: thumbnail || null,
    source_url: source_url || null,
    spec_num: spec_num || null,
  };
}

/**
 * Attach the unified payload to a native drag event.  Idempotent —
 * safe to call after another `setData` for a legacy MIME on the same
 * event.
 */
export function setUnifiedDragPayload(e, payload) {
  try {
    e.dataTransfer.setData(KEYMADE_ITEM_MIME, JSON.stringify(payload));
  } catch (_err) {
    // Safari/Firefox occasionally throw on setData for non-standard
    // MIMEs when the drag was already initiated with certain flags.
    // The legacy payload still travels — don't kill the whole drag.
    void _err;
  }
}

/**
 * Read + parse the unified payload off a drop event.  Returns null if
 * absent or malformed — the caller falls through to legacy payload
 * branches.
 */
export function readUnifiedDragPayload(e) {
  const raw = e?.dataTransfer?.getData?.(KEYMADE_ITEM_MIME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.item_id || !parsed.source_store || !parsed.type) return null;
    return parsed;
  } catch (_err) {
    void _err;
    return null;
  }
}

/**
 * Convert a unified payload into a Work Table item ready to be
 * appended to the current board's items[] array.  Stores the
 * denormalized snapshot alongside `ref_item_id` + `source_store` so
 * the item renders via Phase B native cards without a server call and
 * can be RESOLVED to its canonical item later.
 *
 * `idFactory` defaults to a timestamped random id but tests may pass a
 * deterministic factory.
 */
export function unifiedPayloadToWorkTableItem(
  payload,
  { x = 100, y = 100, zIndex = 0, idFactory = null } = {},
) {
  const makeId = idFactory || (() => `item-${Date.now()}-${Math.floor(Math.random() * 10000)}`);
  const base = {
    id: makeId(),
    x,
    y,
    z_index: zIndex,
    // Phase C2 reference metadata — the two fields that turn a WT item
    // into a pointer into the shared store.  Never dereferenced by the
    // server today; render still uses the snapshot below.
    ref_item_id: payload.item_id,
    source_store: payload.source_store,
    source_url: payload.source_url || undefined,
    spec_num: payload.spec_num || undefined,
    // DP80 Part 4 — which placement path put this here, and when. The
    // reference (`ref_item_id` + `source_store`) says WHAT it is; these two
    // say HOW it arrived, which is what makes an unexplained placement
    // traceable later.
    placed_via: 'drop',
    placed_at: new Date().toISOString(),
    title: payload.name || '',
  };

  const t = payload.type;
  if (t === 'image' || t === 'clip' || t === 'material' || t === 'color') {
    // Every visual type renders as an image card in Phase B (color
    // uses the thumbnail if the source provided one, otherwise the
    // color swatch is captured in source_url / extra fields on the
    // canonical item).
    return {
      ...base,
      item_type: 'image',
      width: 300,
      height: 200,
      image_url: payload.thumbnail || null,
    };
  }
  if (t === 'file') {
    return {
      ...base,
      item_type: 'file',
      width: 280,
      height: 120,
      file_type: 'document',
      // File items also carry a thumbnail if the source has one; the
      // Phase B file card falls back to an icon otherwise.
      image_url: payload.thumbnail || null,
    };
  }
  if (t === 'note') {
    return {
      ...base,
      item_type: 'note',
      width: 240,
      height: 160,
      content: payload.name || '',
    };
  }
  if (t === 'link') {
    return {
      ...base,
      item_type: 'link',
      width: 280,
      height: 100,
      url: payload.source_url || '',
      url_title: payload.name || payload.source_url || '',
    };
  }
  // Unknown type — treat as image with the thumbnail so it still
  // lands as a card rather than silently disappearing.
  return {
    ...base,
    item_type: 'image',
    width: 300,
    height: 200,
    image_url: payload.thumbnail || null,
  };
}
